import { useCallback, useEffect, useRef, useState } from 'react';
import {
  lerLargurasColunaPersistidas,
  salvarLargurasColunaPersistidas,
  LARGURA_COLUNA_PADRAO,
  LARGURA_COLUNA_MIN,
  LARGURA_COLUNA_MAX,
  PREFIXO_LARGURAS_SOLICITACOES,
} from './solicitacoesKanban.utils';

/**
 * Arraste de largura por coluna do Kanban (mesmo padrão do modo "Dividida", ver
 * SolicitacoesSplitView): um único listener global de mousemove/mouseup, guardando qual
 * coluna está sendo arrastada (e a largura/posição inicial dela) numa ref — só persiste no
 * localStorage ao soltar o mouse, não a cada pixel movido.
 */
export function useSolicitacoesKanbanLargura(uid: string | undefined, prefixo: string = PREFIXO_LARGURAS_SOLICITACOES) {
  const [largurasColuna, setLargurasColuna] = useState(() => lerLargurasColunaPersistidas(uid, prefixo));
  const largurasColunaRef = useRef(largurasColuna);
  useEffect(() => {
    largurasColunaRef.current = largurasColuna;
  }, [largurasColuna]);

  const arrasteRef = useRef<{ chave: string; startX: number; startWidth: number } | null>(null);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const arraste = arrasteRef.current;
    if (!arraste) return;
    const novaLargura = Math.min(
      LARGURA_COLUNA_MAX,
      Math.max(LARGURA_COLUNA_MIN, arraste.startWidth + (event.clientX - arraste.startX))
    );
    setLargurasColuna((atual) => ({ ...atual, [arraste.chave]: novaLargura }));
  }, []);

  const pararArraste = useCallback(() => {
    if (!arrasteRef.current) return;
    arrasteRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    salvarLargurasColunaPersistidas(uid, largurasColunaRef.current, prefixo);
  }, [uid, prefixo]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', pararArraste);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', pararArraste);
    };
  }, [handleMouseMove, pararArraste]);

  const iniciarArrasteLargura = useCallback((chave: string, event: React.MouseEvent) => {
    arrasteRef.current = {
      chave,
      startX: event.clientX,
      startWidth: largurasColunaRef.current[chave] ?? LARGURA_COLUNA_PADRAO,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  return { largurasColuna, iniciarArrasteLargura };
}
